import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import DrillAssignmentForm from '../components/DrillAssignmentForm';
import StatsRecordingForm from '../components/StatsRecordingForm';
import PlayerCard from '../components/cards/PlayerCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import DashboardNavigation, { AvatarMenu } from '../components/DashboardNavigation';
import MobileHeaderDropdown from '../components/MobileHeaderDropdown';
import DrillLibraryManager from '../components/DrillLibraryManager';
import AssignmentTrackingTab from '../components/AssignmentTrackingTab';
import ParentInviteManager from '../components/ParentInviteManager';
import PlayerTagManager from '../components/PlayerTagManager';
import DrillRoutineManager from '../components/DrillRoutineManager';
import RoutineAssignmentForm from '../components/RoutineAssignmentForm';
import StatCategoryManager from '../components/StatCategoryManager';
import PerformanceMetricsDashboard from '../components/PerformanceMetricsDashboard';
import DashboardTabbedNavigation from '../components/DashboardTabbedNavigation';
import ImpersonationBanner from '../components/ImpersonationBanner';
import Footer from '../components/Footer';

const CoachDashboardPage = () => {
  const { profile } = useAuth();
  const { isDesktop } = useResponsive();
  const [activeSection, setActiveSection] = useState('roster');
  const [activeSubTab, setActiveSubTab] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDrillAssignment, setShowDrillAssignment] = useState(false);
  const [showStatsRecording, setShowStatsRecording] = useState(false);
  const [showRoutineAssignment, setShowRoutineAssignment] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [expandedPlayerId, setExpandedPlayerId] = useState(null);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const isAdmin = profile?.role === 'admin';

  // Dashboard sections configuration
  const sections = [
    { id: 'roster', label: 'Team Roster' },
    {
      id: 'drills',
      label: 'Drills',
      subTabs: [
        { id: 'library', label: 'Drill Library' },
        { id: 'routines', label: 'Drill Routines' },
      ],
    },
    {
      id: 'performance',
      label: 'Performance',
      subTabs: [
        { id: 'tracking', label: 'Assignment Tracking' },
        { id: 'metrics', label: 'Performance Metrics' },
      ],
    },
    {
      id: 'team-mgmt',
      label: 'Team Management',
      subTabs: [
        { id: 'parent-invites', label: 'Parent Invites' },
        { id: 'player-tags', label: 'Player Tags' },
      ],
    },
    {
      id: 'org-settings',
      label: 'Organization Settings',
      subTabs: [{ id: 'stat-categories', label: 'Stat Categories' }],
    },
  ];

  useEffect(() => {
    loadTeams();
    loadDrills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      loadPlayers(selectedTeam.id);
    }
  }, [selectedTeam]);

  const loadTeams = async () => {
    try {
      setLoading(true);

      if (isAdmin) {
        // Admins see all teams
        const { data, error } = await supabase
          .from('teams')
          .select('*, organizations(name)')
          .order('name');

        if (error) throw error;
        setTeams(data || []);
        if (data && data.length > 0) {
          setSelectedTeam(data[0]);
        }
      } else {
        // Coaches see only their teams
        const { data, error } = await supabase
          .from('team_coaches')
          .select('teams(*, organizations(name))')
          .eq('coach_id', profile.id);

        if (error) throw error;
        const coachTeams = data?.map((tc) => tc.teams) || [];
        setTeams(coachTeams);
        if (coachTeams.length > 0) {
          setSelectedTeam(coachTeams[0]);
        }
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async (teamId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('team_id', teamId)
        .eq('role', 'player')
        .order('full_name');

      if (error) throw error;

      // Fetch drill completion stats and tags for each player
      const playersWithStats = await Promise.all(
        (data || []).map(async (player) => {
          // Fetch drill stats
          const { data: drillsData, error: drillsError } = await supabase
            .from('assigned_drills')
            .select('id, completed')
            .eq('player_id', player.id);

          if (drillsError) {
            console.error('Error loading drill stats for player:', player.id, drillsError);
          }

          const totalDrills = drillsData?.length || 0;
          const completedDrills = drillsData?.filter((d) => d.completed).length || 0;

          // Fetch player tags
          const { data: tagsData, error: tagsError } = await supabase
            .from('player_tag_assignments')
            .select(
              `
              tag:player_tags(id, name, color)
            `
            )
            .eq('player_id', player.id);

          if (tagsError) {
            console.error('Error loading tags for player:', player.id, tagsError);
          }

          return {
            ...player,
            drillStats: {
              total: totalDrills,
              completed: completedDrills,
            },
            tags: tagsData?.map((t) => t.tag).filter(Boolean) || [],
          };
        })
      );

      setPlayers(playersWithStats);
    } catch (error) {
      console.error('Error loading players:', error);
      setError(error.message);
    }
  };

  const loadDrills = async () => {
    try {
      const { data, error } = await supabase.from('drills').select('*').order('name');

      if (error) throw error;
      setDrills(data || []);
    } catch (error) {
      console.error('Error loading drills:', error);
      setError(error.message);
    }
  };

  const handleAssignDrills = (player) => {
    setSelectedPlayer(player);
    setShowDrillAssignment(true);
    setShowStatsRecording(false);
  };

  const handleRecordStats = (player) => {
    setSelectedPlayer(player);
    setShowStatsRecording(true);
    setShowDrillAssignment(false);
  };

  const handleCloseForm = () => {
    setShowDrillAssignment(false);
    setShowStatsRecording(false);
    setSelectedPlayer(null);
  };

  const loadAvailableTags = async () => {
    if (!profile?.organization_id) return;

    try {
      const { data, error } = await supabase
        .from('player_tags')
        .select('id, name, color')
        .eq('organization_id', profile.organization_id)
        .order('name');

      if (error) throw error;
      setAvailableTags(data || []);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  };

  const handleEditPlayer = (player) => {
    setEditingPlayerId(player.id);
    setEditFormData({
      jersey_number: player.jersey_number || '',
      positions: player.positions || [],
      tagIds: player.tags?.map((t) => t.id) || [],
    });
  };

  const handleCancelEdit = () => {
    setEditingPlayerId(null);
    setEditFormData({});
  };

  const handleSavePlayer = async (playerId) => {
    try {
      setError('');

      // Update profile with jersey_number and positions
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          jersey_number: editFormData.jersey_number || null,
          positions: editFormData.positions.length > 0 ? editFormData.positions : null,
        })
        .eq('id', playerId);

      if (profileError) throw profileError;

      // Get current tag assignments for this player
      const { data: currentAssignments, error: fetchError } = await supabase
        .from('player_tag_assignments')
        .select('tag_id')
        .eq('player_id', playerId);

      if (fetchError) throw fetchError;

      const currentTagIds = currentAssignments?.map((a) => a.tag_id) || [];
      const newTagIds = editFormData.tagIds || [];

      // Tags to remove (in current but not in new)
      const tagsToRemove = currentTagIds.filter((id) => !newTagIds.includes(id));

      // Tags to add (in new but not in current)
      const tagsToAdd = newTagIds.filter((id) => !currentTagIds.includes(id));

      // Remove old tag assignments
      if (tagsToRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('player_tag_assignments')
          .delete()
          .eq('player_id', playerId)
          .in('tag_id', tagsToRemove);

        if (deleteError) throw deleteError;
      }

      // Add new tag assignments
      if (tagsToAdd.length > 0) {
        const newAssignments = tagsToAdd.map((tagId) => ({
          player_id: playerId,
          tag_id: tagId,
          assigned_by: profile.id,
        }));

        const { error: insertError } = await supabase
          .from('player_tag_assignments')
          .insert(newAssignments);

        if (insertError) throw insertError;
      }

      // Reload players to show updates
      if (selectedTeam) {
        await loadPlayers(selectedTeam.id);
      }

      setEditingPlayerId(null);
      setEditFormData({});
      setSuccessMessage('Player updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving player:', err);
      setError(err.message);
    }
  };

  const handlePositionToggle = (position) => {
    setEditFormData((prev) => ({
      ...prev,
      positions: prev.positions.includes(position)
        ? prev.positions.filter((p) => p !== position)
        : [...prev.positions, position],
    }));
  };

  const handleTagToggle = (tagId) => {
    setEditFormData((prev) => ({
      ...prev,
      tagIds: prev.tagIds.includes(tagId)
        ? prev.tagIds.filter((id) => id !== tagId)
        : [...prev.tagIds, tagId],
    }));
  };

  // Load available tags when component mounts or profile changes
  useEffect(() => {
    loadAvailableTags();
  }, [profile?.organization_id]);

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
    },
    header: {
      backgroundColor: 'white',
      borderBottom: '1px solid #e0e0e0',
      padding: isDesktop ? '20px 40px' : '16px',
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      justifyContent: 'space-between',
      alignItems: isDesktop ? 'center' : 'stretch',
      gap: isDesktop ? '0' : '12px',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerLogo: {
      height: isDesktop ? '56px' : '45px',
      width: isDesktop ? 'auto' : '45px',
      objectFit: 'contain',
    },
    mobileHeaderContainer: {
      display: 'grid',
      gridTemplateColumns: '45px 1fr 45px',
      alignItems: 'center',
      width: '100%',
      gap: '12px',
    },
    mobileHeaderTitle: {
      textAlign: 'center',
      overflow: 'visible',
    },
    title: {
      fontSize: isDesktop ? '24px' : '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    headerButtons: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      gap: '10px',
    },
    button: {
      padding: isDesktop ? '10px 20px' : '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    primaryButton: {
      backgroundColor: '#007bff',
    },
    successButton: {
      backgroundColor: '#28a745',
    },
    warningButton: {
      backgroundColor: '#ffc107',
      color: '#333',
    },
    dangerButton: {
      backgroundColor: '#dc3545',
    },
    content: {
      padding: isDesktop ? '40px' : '16px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '30px' : '16px',
      marginBottom: isDesktop ? '20px' : '12px',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    cardTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    teamSelector: {
      padding: '10px',
      fontSize: '16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      backgroundColor: 'white',
      minWidth: '250px',
    },
    tableContainer: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      maxWidth: '100%',
      width: '100%',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '600px',
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      borderBottom: '2px solid #e0e0e0',
      fontWeight: '600',
      color: '#666',
      fontSize: '14px',
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #f0f0f0',
      fontSize: '14px',
      maxWidth: '200px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
    },
    smallButton: {
      padding: isDesktop ? '6px 12px' : '10px 16px',
      fontSize: isDesktop ? '12px' : '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #fcc',
      marginBottom: '20px',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#999',
    },
    // Mobile card styles
    playerCardsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (teams.length === 0) {
    return (
      <div style={styles.container}>
        <ImpersonationBanner />
        <header style={styles.header}>
          {isDesktop ? (
            <>
              <div style={styles.headerLeft}>
                <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
                <h1 style={styles.title}>Coach Dashboard</h1>
              </div>
              <DashboardNavigation />
            </>
          ) : (
            <div style={styles.mobileHeaderContainer}>
              <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
              <div style={styles.mobileHeaderTitle}>
                <MobileHeaderDropdown />
              </div>
              <AvatarMenu />
            </div>
          )}
        </header>
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.emptyState}>
              <h2>No Teams Assigned</h2>
              <p>You are not assigned to any teams yet. Please contact your administrator.</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <ImpersonationBanner />
      <header style={styles.header}>
        {isDesktop ? (
          <>
            <div style={styles.headerLeft}>
              <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
              <h1 style={styles.title}>Coach Dashboard</h1>
            </div>
            <DashboardNavigation />
          </>
        ) : (
          <div style={styles.mobileHeaderContainer}>
            <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
            <div style={styles.mobileHeaderTitle}>
              <MobileHeaderDropdown />
            </div>
            <AvatarMenu />
          </div>
        )}
      </header>

      {/* Tab Navigation */}
      <DashboardTabbedNavigation
        sections={sections}
        activeSection={activeSection}
        activeSubTab={activeSubTab}
        onSectionChange={setActiveSection}
        onSubTabChange={setActiveSubTab}
        isDesktop={isDesktop}
      />

      <div style={styles.content}>
        {error && <div style={styles.error}>{error}</div>}
        {successMessage && <div style={styles.success}>{successMessage}</div>}

        {/* Team Roster Tab */}
        {activeSection === 'roster' && (
          <>
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Select Team</h2>
                <select
                  style={styles.teamSelector}
                  value={selectedTeam?.id || ''}
                  onChange={(e) => {
                    const team = teams.find((t) => t.id === e.target.value);
                    setSelectedTeam(team);
                  }}
                >
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name} {team.organizations?.name && `- ${team.organizations.name}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedTeam && (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h2 style={styles.cardTitle}>
                    Team Roster: {selectedTeam.name} ({players.length} players)
                  </h2>
                  <button
                    onClick={() => {
                      setActiveSection('team-mgmt');
                      setActiveSubTab('player-tags');
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#8B5CF6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  >
                    Manage Tags
                  </button>
                </div>

                {players.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p>No players assigned to this team yet.</p>
                  </div>
                ) : isDesktop ? (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.th, width: '60px' }}>Jersey</th>
                          <th style={styles.th}>Name</th>
                          <th style={styles.th}>Positions</th>
                          <th style={styles.th}>Tags</th>
                          <th style={styles.th}>Actions</th>
                          <th style={{ ...styles.th, width: '60px', textAlign: 'center' }}>
                            Contact
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {players.map((player) => {
                          const isEditing = editingPlayerId === player.id;
                          const commonPositions = [
                            'P',
                            'C',
                            '1B',
                            '2B',
                            '3B',
                            'SS',
                            'LF',
                            'CF',
                            'RF',
                            'DH',
                          ];

                          return (
                            <React.Fragment key={player.id}>
                              <tr>
                                {/* Jersey Number */}
                                <td
                                  style={{ ...styles.td, textAlign: 'center', fontWeight: '600' }}
                                >
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editFormData.jersey_number}
                                      onChange={(e) =>
                                        setEditFormData({
                                          ...editFormData,
                                          jersey_number: e.target.value,
                                        })
                                      }
                                      placeholder="#"
                                      style={{
                                        width: '50px',
                                        padding: '4px',
                                        textAlign: 'center',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                      }}
                                    />
                                  ) : (
                                    player.jersey_number || '-'
                                  )}
                                </td>

                                {/* Name */}
                                <td style={styles.td}>{player.full_name || 'Not set'}</td>

                                {/* Positions */}
                                <td style={styles.td}>
                                  {isEditing ? (
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '4px',
                                        maxWidth: '250px',
                                      }}
                                    >
                                      {commonPositions.map((pos) => (
                                        <label
                                          key={pos}
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '2px 6px',
                                            backgroundColor: editFormData.positions.includes(pos)
                                              ? '#007bff'
                                              : '#f0f0f0',
                                            color: editFormData.positions.includes(pos)
                                              ? 'white'
                                              : '#333',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={editFormData.positions.includes(pos)}
                                            onChange={() => handlePositionToggle(pos)}
                                            style={{ display: 'none' }}
                                          />
                                          {pos}
                                        </label>
                                      ))}
                                    </div>
                                  ) : player.positions && player.positions.length > 0 ? (
                                    player.positions.join(', ')
                                  ) : (
                                    '-'
                                  )}
                                </td>

                                {/* Tags */}
                                <td style={styles.td}>
                                  {isEditing ? (
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '4px',
                                        maxWidth: '250px',
                                      }}
                                    >
                                      {availableTags.map((tag) => (
                                        <label
                                          key={tag.id}
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '2px 8px',
                                            backgroundColor: editFormData.tagIds.includes(tag.id)
                                              ? tag.color
                                              : '#f0f0f0',
                                            color: editFormData.tagIds.includes(tag.id)
                                              ? 'white'
                                              : '#333',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={editFormData.tagIds.includes(tag.id)}
                                            onChange={() => handleTagToggle(tag.id)}
                                            style={{ display: 'none' }}
                                          />
                                          {tag.name}
                                        </label>
                                      ))}
                                    </div>
                                  ) : player.tags && player.tags.length > 0 ? (
                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                      {player.tags.map((tag) => (
                                        <span
                                          key={tag.id}
                                          style={{
                                            padding: '2px 8px',
                                            backgroundColor: tag.color,
                                            color: 'white',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                          }}
                                        >
                                          {tag.name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    '-'
                                  )}
                                </td>

                                {/* Actions */}
                                <td style={styles.td}>
                                  {isEditing ? (
                                    <div style={styles.actionButtons}>
                                      <button
                                        style={{
                                          ...styles.smallButton,
                                          backgroundColor: '#28a745',
                                          color: 'white',
                                        }}
                                        onClick={() => handleSavePlayer(player.id)}
                                      >
                                        Save
                                      </button>
                                      <button
                                        style={{
                                          ...styles.smallButton,
                                          backgroundColor: '#6c757d',
                                          color: 'white',
                                        }}
                                        onClick={handleCancelEdit}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <div style={styles.actionButtons}>
                                      <button
                                        style={{
                                          ...styles.smallButton,
                                          backgroundColor: '#ffc107',
                                          color: '#333',
                                        }}
                                        onClick={() => handleEditPlayer(player)}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        style={{
                                          ...styles.smallButton,
                                          backgroundColor: '#007bff',
                                          color: 'white',
                                        }}
                                        onClick={() => handleAssignDrills(player)}
                                      >
                                        Assign Drills
                                      </button>
                                      <button
                                        style={{
                                          ...styles.smallButton,
                                          backgroundColor: '#28a745',
                                          color: 'white',
                                        }}
                                        onClick={() => handleRecordStats(player)}
                                      >
                                        Record Stats
                                      </button>
                                    </div>
                                  )}
                                </td>

                                {/* Contact Expand */}
                                <td style={{ ...styles.td, textAlign: 'center' }}>
                                  <button
                                    onClick={() =>
                                      setExpandedPlayerId(
                                        expandedPlayerId === player.id ? null : player.id
                                      )
                                    }
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: '18px',
                                      color: '#666',
                                      padding: '4px 8px',
                                    }}
                                    title={
                                      expandedPlayerId === player.id
                                        ? 'Hide contact info'
                                        : 'Show contact info'
                                    }
                                  >
                                    {expandedPlayerId === player.id ? '▼' : '▶'}
                                  </button>
                                </td>
                              </tr>
                              {expandedPlayerId === player.id && (
                                <tr>
                                  <td
                                    colSpan="6"
                                    style={{
                                      ...styles.td,
                                      backgroundColor: '#f8f9fa',
                                      padding: '12px 16px',
                                    }}
                                  >
                                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                      <div>
                                        <strong style={{ fontSize: '13px', color: '#666' }}>
                                          Email:
                                        </strong>
                                        <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                                          {player.email}
                                        </span>
                                      </div>
                                      <div>
                                        <strong style={{ fontSize: '13px', color: '#666' }}>
                                          Phone:
                                        </strong>
                                        <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                                          {player.phone || 'Not set'}
                                        </span>
                                      </div>
                                      <div>
                                        <strong style={{ fontSize: '13px', color: '#666' }}>
                                          Birth Date:
                                        </strong>
                                        <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                                          {player.birth_date
                                            ? new Date(player.birth_date).toLocaleDateString()
                                            : 'Not set'}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={styles.playerCardsContainer}>
                    {players.map((player) => (
                      <PlayerCard
                        key={player.id}
                        player={player}
                        onAssignDrills={handleAssignDrills}
                        onRecordStats={handleRecordStats}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Drill Library Tab */}
        {activeSection === 'drills' && activeSubTab === 'library' && (
          <DrillLibraryManager profile={profile} />
        )}

        {/* Drill Routines Tab */}
        {activeSection === 'drills' && activeSubTab === 'routines' && (
          <>
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <button
                onClick={() => setShowRoutineAssignment(true)}
                style={{
                  ...styles.button,
                  ...styles.successButton,
                  padding: isDesktop ? '12px 24px' : '14px 20px',
                }}
              >
                Assign Routine
              </button>
            </div>
            <DrillRoutineManager profile={profile} />
          </>
        )}

        {/* Assignment Tracking Tab */}
        {activeSection === 'performance' && activeSubTab === 'tracking' && selectedTeam && (
          <AssignmentTrackingTab
            selectedTeam={selectedTeam}
            coachId={profile.id}
            isDesktop={isDesktop}
            isAdmin={false}
          />
        )}

        {/* Parent Invites Tab */}
        {activeSection === 'team-mgmt' && activeSubTab === 'parent-invites' && (
          <ParentInviteManager profile={profile} teams={teams} />
        )}

        {/* Player Tags Tab */}
        {activeSection === 'team-mgmt' && activeSubTab === 'player-tags' && (
          <PlayerTagManager profile={profile} />
        )}

        {/* Stat Categories Tab */}
        {activeSection === 'org-settings' && activeSubTab === 'stat-categories' && (
          <StatCategoryManager profile={profile} />
        )}

        {/* Performance Metrics Tab */}
        {activeSection === 'performance' && activeSubTab === 'metrics' && (
          <PerformanceMetricsDashboard profile={profile} teams={teams} />
        )}

        {/* Assignment/Stats Forms - Only show on roster tab */}
        {activeSection === 'roster' && (
          <>
            {showDrillAssignment && selectedPlayer && (
              <DrillAssignmentForm
                player={selectedPlayer}
                drills={drills}
                onClose={handleCloseForm}
                coachId={profile.id}
              />
            )}

            {showStatsRecording && selectedPlayer && (
              <StatsRecordingForm
                player={selectedPlayer}
                onClose={handleCloseForm}
                coachId={profile.id}
                organizationId={profile.organization_id}
                isMobile={!isDesktop}
              />
            )}
          </>
        )}

        {/* Routine Assignment Form - Only show on routines tab */}
        {activeSection === 'drills' && activeSubTab === 'routines' && showRoutineAssignment && (
          <RoutineAssignmentForm
            profile={profile}
            teams={teams}
            onClose={() => setShowRoutineAssignment(false)}
            onSuccess={(result) => {
              setSuccessMessage(result.message);
              setTimeout(() => setSuccessMessage(''), 5000);
            }}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CoachDashboardPage;
